import { DeliverySelector } from "./delivery-selector.js";
import { ICmsDeliverySelectorConfiguration } from "./interfaces/cms-delivery-selector-config-interface.js";
import { IDeliverySelectorOutputOption } from "./interfaces/delivery-selector-output-option-interface.js";
import { parseIntOrNull } from "../../utils/number.js";
import { DeliveryApi } from "../../api/estore/checkout/delivery-api.js";
import { IDeliverySelectorData } from "../../api/estore/checkout/interfaces/delivery-selector-data-interface.js";
import { IDeliveryDataRequest } from "../../api/estore/checkout/interfaces/delivery-data-request-interface.js";

// Have to declare this on window object to be able to access it
declare var globalSettings: any;

export interface IDeliverySelectorInputData {
    countryCode: string;
    postalCode: string;
    deliveryDate: string;
}

export interface IDeliveryOptionWithPassThroughData extends IDeliverySelectorOutputOption {
    name: string;
    deliveryID: number;
    deliverOnDays: number[];
    nearestDeliveryDate: string;
    requiresDeliveryDate: boolean;
    carriageTimeHours: number;
}

export class CmsDeliverySelector extends HTMLElement {
    deliverySelector: DeliverySelector = null;
    config: ICmsDeliverySelectorConfiguration;
    passThroughData: IDeliveryOptionWithPassThroughData[];

    constructor(config: ICmsDeliverySelectorConfiguration) {
        super();
        this.setConfiguration(config);

        if (this.config.useShadowRoot) {
            this.attachShadow({ mode: "open" });
        }
    }

    connectedCallback() { }

    async update(inputData: IDeliverySelectorInputData) {
        if (!this.isConnected) {
            return;
        }

        const deliveryApi = new DeliveryApi();
        const requestData = this.createRequestData(inputData);

        // Load delivery groups and options
        const deliveryDetailsResponse = await deliveryApi.getDeliveryDetails(requestData, inputData.countryCode, inputData.postalCode);

        const deliveryDetails = deliveryDetailsResponse.result;

        this.setPassThroughData(deliveryDetails);

        if (this.deliverySelector === null) {
            const userLocales = globalSettings.twoLetterLocaleFallBacks;
            this.initDeliverySelector(deliveryDetails, userLocales);
        } else {
            await this.deliverySelector.update(deliveryDetails);
        }
    }

    selectedOptionChangedDelegatesCache: { (selectedOption: IDeliveryOptionWithPassThroughData) : void } [] = [];
    onSelectedOptionChanged(delegate: { (selectedOption: IDeliveryOptionWithPassThroughData) : void }) {
        const del = (o: IDeliverySelectorOutputOption) => {
            const optionWithPassData = this.getDeliveryOutputOptionWithPassThroughData(o);
            delegate(optionWithPassData);
        };

        if (!this.deliverySelector) {
            this.selectedOptionChangedDelegatesCache.push(del);
        } else {
            this.deliverySelector.onSelectedOptionChanged(del);
        }
    }

    optionPropertyChangedDelegatesCache: { (selectedOption: IDeliveryOptionWithPassThroughData) : void } [] = [];
    onOptionPropertyChanged(delegate: { (selectedOption: IDeliveryOptionWithPassThroughData) : void }) {
        const del = (o: IDeliverySelectorOutputOption) => {
            const optionWithPassData = this.getDeliveryOutputOptionWithPassThroughData(o);
            delegate(optionWithPassData);
        };

        if (!this.deliverySelector) {
            this.optionPropertyChangedDelegatesCache.push(del);
        } else {
            this.deliverySelector.onOptionPropertyChanged(del);
        }
    }

    private getDeliveryOutputOptionWithPassThroughData(option: IDeliverySelectorOutputOption) : IDeliveryOptionWithPassThroughData {
        let withPassData = {...option} as IDeliveryOptionWithPassThroughData;
        const passData = this.passThroughData.filter(pd => pd.id === withPassData.id)[0];
        withPassData.deliveryID = passData.deliveryID;
        withPassData.name = passData.name;
        withPassData.carriageTimeHours = passData.carriageTimeHours;
        withPassData.deliverOnDays = passData.deliverOnDays;
        withPassData.nearestDeliveryDate = passData.nearestDeliveryDate;
        withPassData.requiresDeliveryDate = passData.requiresDeliveryDate;

        return withPassData;
    }

    private setPassThroughData(data: IDeliverySelectorData) {
        this.passThroughData = data.groups.map(g => g.options).flat().map(o => {
            let passData = o as any;
            return {
                id: passData.id,
                deliveryID: parseIntOrNull(passData.id) ?? -1,
                name: passData.title,
                carriageTimeHours: passData.carriageTimeHours,
                deliverOnDays: passData.deliverOnDays,
                nearestDeliveryDate: passData.nearestDeliveryDate,
                requiresDeliveryDate: passData.requiresDeliveryDate
            } as IDeliveryOptionWithPassThroughData;
        });
    }

    private initDeliverySelector(data: IDeliverySelectorData, userLocales: any) {
        this.deliverySelector = new DeliverySelector(this.config, data, userLocales);
        this.config.useShadowRoot ? this.shadowRoot.append(this.deliverySelector) : this.append(this.deliverySelector);
        this.selectedOptionChangedDelegatesCache.forEach(d => this.deliverySelector.onSelectedOptionChanged(d));
        this.optionPropertyChangedDelegatesCache.forEach(d => this.deliverySelector.onOptionPropertyChanged(d));
    }

    readonly SHOW_ALL_KEY = "ShowAll";
    getShowAll() {
        const query = new URLSearchParams(window.location.search);
        return query.has(this.SHOW_ALL_KEY) && ["true", "1"].includes(query.get(this.SHOW_ALL_KEY).toLowerCase());
    }

    createRequestData(inputData: IDeliverySelectorInputData) : IDeliveryDataRequest {
        return {
            groupIconWidth: this.config.groupIconWidth,
            optionIconWidth: this.config.optionIconWidth,
            deliveryDate: inputData?.deliveryDate,
            forceShowAll: this.getShowAll(),
            showDeliveryTimeInfo: this.config.showDeliveryTimeInfo
        } as IDeliveryDataRequest;
    }

    setConfiguration(config: ICmsDeliverySelectorConfiguration) {
        const basePath = "/components/checkout/delivery/";
        const stylesPath = basePath + "styles/";
        const assetsPath = basePath + "assets/";

        const defaultConfig = {
            groupIconWidth: 100,
            optionIconWidth: 100,
            triggerOptionSelectedOnInit: false,
            showDeliveryTimeInfo: false,
            useShadowRoot: true,
            cssFiles: {
                deliverySelector: stylesPath + "delivery-selector.css",
                groupList: stylesPath + "delivery-group-list.css",
                groupItem: stylesPath + "delivery-group.css",
                optionList: stylesPath + "delivery-option-list.css",
                optionItem: stylesPath + "delivery-option.css",
                pickupPointSelector: stylesPath + "delivery-pickuppoint-selector.css",
                pickupPointItem: stylesPath + "delivery-pickuppoint.css",
                infoIcon: assetsPath + "info.svg",
                chevronDownIcon: assetsPath + "chevron-down.svg",
                chevronUpIcon: assetsPath + "chevron-up.svg"
            }
        } as ICmsDeliverySelectorConfiguration;

        this.config = Object.assign({}, defaultConfig, config);
    }
}

customElements.define('cms-delivery-selector', CmsDeliverySelector);